// Angular imports
import { ComponentFactoryResolver, ComponentRef, Directive, ViewContainerRef, Input, OnInit } from '@angular/core';

// CC-Common imports
import { Utils, DxWidgetUtils } from 'cc-common';

// Component imports
import { ICommunityContextDashboardComponent } from '../_interfaces/community-context-component';
import { IDynamicDashboardComponent } from '../_interfaces/dynamic-dashboard-component';
import { IInteractiveDashboardComponent } from '../_interfaces/interactive-dashboard-component';
import { IEntityBasedDashboardComponent } from '../_interfaces/entity-context-component';

// Service imports
import { DynamicDashboardService } from '../_services/dynamic-dashboard.service';

// Model imports
import { DynamicDashboardItem } from '../_model/dynamic-dashboard-item';
import { DynamicDashboardWidget } from '../_model/dynamic-dashboard-widget';
import { EntitySelectorResultDto } from '../../cirabooks/shared-cirabooks-module/_models/common/enity-selector-result-dto';
import { IContextInfo, IWidgetSize } from '../_interfaces/dynamic-dashboard-data-models';

@Directive({
    selector: '[appDynamicDashboardTemplate]'
})
export class DynamicDashboardTemplateDirective implements OnInit {
    @Input() editMode: boolean;
    @Input() widgetId: string;
    @Input() dashboardItem: DynamicDashboardItem;

    componentRef: ComponentRef<
        | IDynamicDashboardComponent
        | IEntityBasedDashboardComponent
        | ICommunityContextDashboardComponent
        | IInteractiveDashboardComponent
    >;
    dashboardWidgets: DynamicDashboardWidget[];
    dashboardWidget: DynamicDashboardWidget;

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly dashboardService: DynamicDashboardService
    ) {}

    ngOnInit(): void {
        this.dashboardItem.widgetContextChanged.subscribe((context: IContextInfo) => {
            this.updateWidgetContext(context);
        });
        this.dashboardItem.widgetEntityChanged.subscribe((entity: EntitySelectorResultDto) => {
            this.updateWidgetEntity(entity);
        });
        this.dashboardItem.widgetSizeChanged.subscribe((size: IWidgetSize) => {
            this.refreshWidget(size);
        });
        this.dashboardItem.titleUpdate.subscribe((flag) => {
            this.updateTitle();
        });
        this.dashboardService.getDashboardWidgets().subscribe((dashboardWidgets) => {
            this.dashboardWidgets = dashboardWidgets;
            this.createWidget();
        });
    }

    get minWidthRatio(): number {
        return DxWidgetUtils.hasInstance(this.componentRef) ? this.componentRef.instance.minWidthRatio : 0;
    }

    get minHeightRatio(): number {
        return DxWidgetUtils.hasInstance(this.componentRef) ? this.componentRef.instance.minHeightRatio : 0;
    }

    get minWidth(): number {
        return DxWidgetUtils.hasInstance(this.componentRef) ? this.componentRef.instance.minWidth : 0;
    }

    get minHeight(): number {
        return DxWidgetUtils.hasInstance(this.componentRef) ? this.componentRef.instance.minHeight : 0;
    }

    get hideFullScreen(): boolean {
        return DxWidgetUtils.hasInstance(this.componentRef) ? this.componentRef.instance.hideFullScreen : false;
    }

    private createWidget(): void {
        if (Utils.notNullOrEmpty(this.widgetId) && Utils.notNullOrEmpty(this.dashboardWidgets)) {
            let widget = this.dashboardWidgets.find((w) => Utils.equals(w.id, this.widgetId));
            if (Utils.isNullOrUndefined(widget)) {
                widget = this.dashboardService.getMissingWidget();
            }
            if (Utils.notNullAndDefined(widget)) {
                this.dashboardWidget = widget;
                if (Utils.isFalse(this.editMode)) {
                    this.createComponent();
                }
            }
        }
    }

    private createComponent(): void {
        if (Utils.notNullAndDefined(this.dashboardWidget)) {
            this.viewContainerRef.clear();
            if (DxWidgetUtils.hasInstance(this.componentRef)) {
                this.componentRef.destroy();
            }
            setTimeout(() => {
                const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
                    this.dashboardWidget.component
                );
                this.componentRef = this.viewContainerRef.createComponent(componentFactory);
                this.componentRef.instance.hideTitle = true;
                this.componentRef.instance.widgetId = this.dashboardWidget.id;
                this.componentRef.changeDetectorRef.detectChanges();
                this.dashboardItem.minWidthRatio = this.minWidthRatio;
                this.dashboardItem.minHeightRatio = this.minHeightRatio;
                this.dashboardItem.minWidth = this.minWidth;
                this.dashboardItem.minHeight = this.minHeight;
                this.dashboardItem.hideFullScreen = this.hideFullScreen;
            }, 50);
        }
    }

    private updateWidgetContext(context: IContextInfo): void {
        if (
            DxWidgetUtils.hasInstance(this.componentRef) &&
            Utils.notNullAndDefined(this.dashboardWidget) &&
            Utils.isTrue(this.dashboardWidget.hasContext)
        ) {
            (this.componentRef.instance as ICommunityContextDashboardComponent).setCommunity(
                context.communityId,
                context.managementCompanyId,
                context.communityName
            );
        }
    }

    private updateWidgetEntity(entity: EntitySelectorResultDto): void {
        if (
            DxWidgetUtils.hasInstance(this.componentRef) &&
            Utils.notNullAndDefined(this.dashboardWidget) &&
            Utils.isTrue(this.dashboardWidget.hasContext)
        ) {
            (this.componentRef.instance as IEntityBasedDashboardComponent).setEntity(entity.entityId, entity.name);
        }
    }

    private refreshWidget(size: IWidgetSize): void {
        if (
            DxWidgetUtils.hasInstance(this.componentRef) &&
            Utils.notNullAndDefined(this.dashboardWidget) &&
            Utils.isTrue(this.dashboardWidget.isInteractive)
        ) {
            (this.componentRef.instance as IInteractiveDashboardComponent).refresh(size.width, size.height);
        }
    }

    private updateTitle(): void {
        if (
            DxWidgetUtils.hasInstance(this.componentRef) &&
            Utils.notNullAndDefined(this.dashboardWidget) &&
            Utils.isTrue(this.dashboardWidget.isInteractive)
        ) {
            const title = (this.componentRef.instance as IInteractiveDashboardComponent).getTitle(
                this.dashboardWidget.name
            );
            this.dashboardItem.title = title;
        }
    }
}
